import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import attentionCat from '@/shared/assets/images/cats/attention.png';
import { useMedia } from '@/shared/hooks';
import { AuthCard, Button, Portal } from '@/shared/ui';

import { MFAEnabledNotice } from '../MFAEnabledNotice/MFAEnabledNotice';
import { MFAEnrollmentCard } from '../MFAEnrollmentCard/MFAEnrollmentCard';

import styles from './OrganizationMFAEnrollment.module.scss';

type OrganizationMFAEnrollmentStep = 'prompt' | 'setup' | 'enabled';

type OrganizationMFAEnrollmentProps = {
  targetOrganizationName: string;
  className?: string;
  currentOrganizationName?: string;
  onCancel: () => void;
  onSignInRequired: () => void;
  onCompleted: () => void;
};

/**
 * Enrolment demanded by the organization the user is switching into. The
 * switch is held until setup finishes, so cancelling at any point leaves the
 * session in the organization it was already in.
 */
export const OrganizationMFAEnrollment: FC<OrganizationMFAEnrollmentProps> = (
  props,
) => {
  const {
    targetOrganizationName,
    className,
    currentOrganizationName,
    onCancel,
    onSignInRequired,
    onCompleted,
  } = props;

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const [step, setStep] = useState<OrganizationMFAEnrollmentStep>('prompt');

  const cancelLabel = currentOrganizationName ? (
    <FormattedMessage
      id="mfa.organization.cancelAndStay"
      defaultMessage="Cancel and stay in {organizationName}"
      values={{ organizationName: currentOrganizationName }}
    />
  ) : (
    <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
  );

  return (
    <Portal>
      {step === 'enabled' && (
        <MFAEnabledNotice
          fixed
          className={className}
          onDone={onCompleted}
          message={
            <FormattedMessage
              id="mfa.organization.enabled"
              defaultMessage="Two-step verification is enabled. Switching to {organizationName}."
              values={{ organizationName: targetOrganizationName }}
            />
          }
        />
      )}

      {step === 'setup' && (
        <MFAEnrollmentCard
          fixed
          className={className}
          onCompleted={() => setStep('enabled')}
          onCancel={onCancel}
          onSignInRequired={onSignInRequired}
          cancelLabel={cancelLabel}
          eyebrow={
            <FormattedMessage
              id="mfa.organization.switchingTo"
              defaultMessage="Switching to {organizationName}"
              values={{ organizationName: targetOrganizationName }}
            />
          }
          title={
            <FormattedMessage
              id="mfa.setup.title"
              defaultMessage="Set up two-step verification"
            />
          }
        />
      )}

      {step === 'prompt' && (
        <AuthCard
          fixed
          className={className}
          variant="notice"
          dataSection="mfa-organization-prompt"
        >
          <h3 className={isMobile ? 'h5' : 'h3'}>
            <FormattedMessage
              id="mfa.organization.title"
              defaultMessage="{organizationName} requires two-step verification"
              values={{ organizationName: targetOrganizationName }}
            />
          </h3>

          <img
            src={attentionCat}
            width={130}
            height={130}
            alt={formatMessage({
              id: 'imgAltText.attentionCat',
              defaultMessage: 'Attention cat',
            })}
          />

          <p className={cn(styles.description, 'p2')}>
            <FormattedMessage
              id="mfa.organization.description"
              defaultMessage="Set up an authenticator app to work in this organization. It applies to your whole account, so you will be asked for a code at every sign in after that."
            />
          </p>

          {currentOrganizationName && (
            <div className={styles.stayIn}>
              <span className={cn(styles.stayInLabel, 'p3')}>
                <FormattedMessage
                  id="mfa.organization.ifYouCancel"
                  defaultMessage="If you cancel, you stay in"
                />
              </span>

              <span className="p3b">{currentOrganizationName}</span>
            </div>
          )}

          <div className={styles.actions}>
            <Button variant="tertiary" onClick={onCancel}>
              <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
            </Button>

            <Button onClick={() => setStep('setup')}>
              <FormattedMessage
                id="mfa.organization.setUpAndSwitch"
                defaultMessage="Set up and switch"
              />
            </Button>
          </div>
        </AuthCard>
      )}
    </Portal>
  );
};
